import { useMutation, useQueryClient } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import { SEARCH_DATA_KEY } from 'api/search'
import axios from 'axios'
// types
type AllItems = {
  deals: any[]
  organizations: any[]
  products: any[]
}

const computeAfterDelete = (prev: AllItems | undefined, items: { PK: string; SK: string }[]) => {
  if (!prev) return prev
  const skSet = new Set(items.map(i => i.SK))

  // collect removed deal_uuids to prune products too (optional)
  const removedUUIDs = new Set(
    (prev.deals ?? [])
      .filter(d => skSet.has(d.SK))
      .map(d => d.deal_uuid)
      .filter(Boolean)
  )

  const deals = (prev.deals ?? []).filter(d => !skSet.has(d.SK))
  const products = removedUUIDs.size
    ? (prev.products ?? []).filter(p => !removedUUIDs.has(p.deal_uuid))
    : prev.products ?? []

  return { ...prev, deals, products }
}

export interface DeleteItemInput {
  EntityType: 'organization' | 'deal' | string
  items: { PK: string; SK: string }[]
  tableName?: string
}

const deleteDealItems = async (payload: DeleteItemInput) => {
  const res = await axios.delete(`${baseUrl}/aws/delete/deals`, { data: payload })
  if (res.status !== 200) throw new Error(res.data?.message || 'Failed to delete items')
  return res.data
}

export const useDeleteDealItems = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: deleteDealItems,

    // ✅ Optimistic: remove from cache immediately (no refetch)
    onMutate: async variables => {
      // console.log('variables', variables)
      await queryClient.cancelQueries({ queryKey: SEARCH_DATA_KEY })

      const previous = queryClient.getQueryData<AllItems>(SEARCH_DATA_KEY)
      const next = computeAfterDelete(previous, variables.items)

      queryClient.setQueryData(SEARCH_DATA_KEY, next)

      // Also purge detail caches so nothing refetches them later by accident (still no invalidation)
      const toRemoveSK = new Set(variables.items.map(i => i.SK))
      const removed = (previous?.deals ?? []).filter(d => toRemoveSK.has(d.SK))
      for (const d of removed) {
        if (d.deal_uuid) queryClient.removeQueries({ queryKey: ['deal', d.deal_uuid], exact: true })
        queryClient.removeQueries({ queryKey: ['deal', d.PK, d.SK], exact: true })
      }

      return { previous }
    },

    // ❌ Rollback if API fails
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) {
        queryClient.setQueryData(SEARCH_DATA_KEY, ctx.previous)
      }
      console.error('[useDeleteItems] Deletion error:', _err)
    },

    // ✅ Success: nothing else to do; optimistic state is already correct
    onSuccess: () => {
      // do not invalidate; we intentionally avoid refetch
    },
  })
}
